<template>
  <div
    class="flex flex-wrap items-center gap-x-3.5 gap-y-2.5 rounded-lg border border-lightGray30 bg-white p-3"
    :data-testid="testId"
  >
    <div class="flex flex-col gap-1">
      <span class="text-[11px] uppercase tracking-wider text-neutral50">
        Período (horário de Brasília)
      </span>
      <div class="flex flex-wrap gap-1">
        <button
          v-for="preset in PRIORITY_PERIOD_PRESETS"
          :key="preset.value"
          type="button"
          class="rounded-full border px-2.5 py-1 text-[13px]"
          :class="chipClass(filters.preset === preset.value)"
          :aria-pressed="filters.preset === preset.value"
          :data-testid="`${testId}-preset-${preset.value}`"
          @click="onPreset(preset.value)"
        >
          {{ preset.label }}
        </button>
      </div>
      <span
        v-if="cycleLabel"
        class="font-mono text-xs text-neutral50"
        :title="listedUntilText ?? undefined"
        :data-testid="`${testId}-cycle-label`"
      >
        {{ cycleLabel }}
        <span v-if="listedUntilText" :data-testid="`${testId}-listed-until`">
          {{ listedUntilText }}
        </span>
      </span>
      <DateFilter
        v-if="filters.preset === 'CUSTOM'"
        title="Período"
        :is-range="true"
        :model-value="customRange"
        :data-testid="`${testId}-custom-range`"
        @select="onCustomRange"
      />
    </div>

    <SelectFilter
      label="Grupo operacional"
      :is-open="openFilter === 'group'"
      :options="groupOptions"
      :multiple="true"
      :searchable="true"
      :selected-value="filters.operationalGroups"
      :data-testid="`${testId}-group`"
      @toggle="toggleOpen('group')"
      @select="onToggleGroup"
    />

    <SelectFilter
      label="Unidade"
      :is-open="openFilter === 'unit'"
      :options="unitOptions"
      :multiple="true"
      :searchable="true"
      :selected-value="filters.healthUnits.map(String)"
      :data-testid="`${testId}-unit`"
      @toggle="toggleOpen('unit')"
      @select="onToggleUnit"
    />

    <SelectFilter
      label="Setor"
      :is-open="openFilter === 'sector'"
      :options="sectorSelectOptions"
      :multiple="true"
      :searchable="true"
      :selected-value="filters.sectors.map(String)"
      :data-testid="`${testId}-sector`"
      @toggle="toggleOpen('sector')"
      @select="onToggleSector"
    />

    <SelectFilter
      label="Posição"
      :is-open="openFilter === 'position'"
      :options="positionSelectOptions"
      :multiple="true"
      :searchable="true"
      :selected-value="selectedPositions"
      :data-testid="`${testId}-position`"
      @toggle="toggleOpen('position')"
      @select="onTogglePosition"
    />

    <SelectFilter
      label="Escala"
      :is-open="openFilter === 'schedule'"
      :options="scheduleOptions"
      :multiple="true"
      :searchable="true"
      :selected-value="filters.schedules.map(String)"
      :data-testid="`${testId}-schedule`"
      @toggle="toggleOpen('schedule')"
      @select="onToggleSchedule"
    />

    <SelectFilter
      label="Médico"
      :is-open="openFilter === 'doctor'"
      :options="doctorSelectOptions"
      :multiple="true"
      :searchable="true"
      :selected-value="filters.doctorIds.map(String)"
      :data-testid="`${testId}-doctor`"
      @toggle="toggleOpen('doctor')"
      @select="onToggleDoctor"
    />

    <div class="flex items-center gap-1">
      <DateFilter
        ref="stationDateFilter"
        :title="stationDateTitle"
        :is-range="true"
        :model-value="stationDateRange"
        :data-testid="`${testId}-station-date`"
        @select="onStationDate"
      />
      <button
        v-if="filters.stationDateFrom || filters.stationDateTo"
        type="button"
        class="rounded-full border border-lightGray30 px-2 py-0.5 text-xs text-neutral50"
        aria-label="Limpar data do plantão"
        :data-testid="`${testId}-station-date-clear`"
        @click="onClearStationDate"
      >
        ×
      </button>
    </div>

    <SelectFilter
      label="Situação"
      :is-open="openFilter === 'situation'"
      :options="situationOptions"
      :multiple="true"
      :selected-value="selectedSituationKeys"
      :data-testid="`${testId}-situation`"
      @toggle="toggleOpen('situation')"
      @select="onToggleSituationGroup"
    />

    <SelectFilter
      label="Estado do SLA"
      :is-open="openFilter === 'slaState'"
      :options="SLA_STATE_OPTIONS"
      :multiple="true"
      :selected-value="selectedSlaValues"
      :data-testid="`${testId}-sla-state`"
      @toggle="toggleOpen('slaState')"
      @select="onToggleSlaState"
    />

    <SelectFilter
      label="Check"
      :is-open="openFilter === 'check'"
      :options="CHECK_OPTIONS"
      :selected-value="filters.checkStatus ?? ''"
      :data-testid="`${testId}-check`"
      @toggle="toggleOpen('check')"
      @select="onCheck"
    />

    <div class="flex flex-col gap-1">
      <label class="text-[11px] uppercase tracking-wider text-neutral50" :for="`${testId}-search`">
        Médico ou ID
      </label>
      <input
        :id="`${testId}-search`"
        type="search"
        placeholder="Buscar"
        class="w-44 rounded-md border border-lightGray30 px-2 py-1 text-[13px] text-textPrimary"
        :value="searchText"
        maxlength="200"
        :data-testid="`${testId}-search`"
        @input="onSearchInput(($event.target as HTMLInputElement).value)"
      />
    </div>

    <div class="flex flex-col gap-1">
      <span class="text-[11px] uppercase tracking-wider text-neutral50">Atalhos</span>
      <div class="flex flex-wrap gap-1">
        <!-- A2 (25/09): contador de auditoria que NÃO filtra a tela — o clique
             baixa o xlsx da lista (`list=ANTECIPE_PAID_UNCONFIRMED`). Sempre
             visível; com 0 (ou sem resposta) fica desabilitado. -->
        <button
          type="button"
          class="rounded-full border border-lightGray30 bg-white px-2.5 py-1 text-[13px] text-neutral50 disabled:cursor-not-allowed disabled:opacity-60"
          title="Exporta a lista (xlsx)"
          :aria-label="`Antecipe pago sem horário: ${antecipePaidUnconfirmedLabel}. Exporta a lista (xlsx)`"
          :disabled="isExporting || !antecipePaidUnconfirmedCount"
          :aria-busy="isExporting ? 'true' : undefined"
          :data-testid="`${testId}-quick-antecipe-unconfirmed`"
          @click="emit('export-antecipe-paid-unconfirmed')"
        >
          Antecipe pago sem horário
          <b class="tabular-nums" :data-testid="`${testId}-quick-antecipe-unconfirmed-count`">{{
            antecipePaidUnconfirmedLabel
          }}</b>
        </button>
        <button
          v-for="quick in visibleQuickFilters"
          :key="quick.key"
          type="button"
          class="rounded-full border px-2.5 py-1 text-[13px]"
          :class="chipClass(quick.isOn(filters))"
          :aria-pressed="quick.isOn(filters)"
          :data-testid="`${testId}-quick-${quick.key}`"
          @click="updateFilters(quick.toggle(filters))"
        >
          {{ quick.label }}
        </button>
      </div>
    </div>

    <span class="flex-1" />

    <div class="flex items-center gap-2 text-xs text-neutral50">
      <span v-if="lastLoadedLabel">
        Atualizado {{ lastLoadedLabel }} · automático a cada 60 s
      </span>
      <button
        type="button"
        class="rounded-md border border-lightGray30 px-3 py-1.5 text-[13px] font-medium text-textPrimary"
        :disabled="isRefreshing"
        :data-testid="`${testId}-refresh`"
        @click="emit('refresh')"
      >
        Atualizar
      </button>
      <button
        type="button"
        class="rounded-md border border-primary bg-primary px-3 py-1.5 text-[13px] font-medium text-white"
        :disabled="isExporting"
        :data-testid="`${testId}-export`"
        @click="emit('export')"
      >
        Exportar xlsx
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import DateFilter from '@/components/Filters/DateFilter.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import { useErrorToast } from '@/composables/useErrorToast'
import Network from '@/Network'
import FieldOptionsService from '@/Network/Services/FieldOptionsService'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import { parseLocalDate, toDateOnly } from '@/utils/date'
import type {
  PriorityCheckStatus,
  PriorityDoctorFacetDto,
  PriorityPositionOption,
  PrioritySectorOption,
  PrioritySituation,
  PrioritySituationCounts,
  PrioritySlaState
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

import { PRIORITY_CHECK_LABELS, PRIORITY_SITUATION_GROUPS } from '../constants'
import {
  buildPositionSelectOptions,
  buildSectorSelectOptions,
  prunedSectorsForUnits,
  selectedPositionValues,
  toggleGroup,
  togglePositionOption
} from '../filterOptions'
import {
  PRIORITY_MIN_REJECTIONS,
  PRIORITY_PERIOD_PRESETS,
  PRIORITY_SLA_STATES,
  PRIORITY_STALLED_HOURS,
  lastSevenDaysRange
} from '../filters'
import { PRIORITY_SLA_PENDING_STATES, PRIORITY_SLA_STATE_LABELS, formatSlaHour } from '../slaFormat'
import type { PriorityPaymentsFilters, PriorityPeriodPreset } from '../filters'

const props = withDefaults(
  defineProps<{
    filters: PriorityPaymentsFilters
    /** `cycle.label` do servidor, ex.: `'sex 18/09 07:00 → seg 21/09 07:00'`. */
    cycleLabel: string | null
    /**
     * `cycle.listedUntil` (B1, 25/09): corte ISO do bloco "À vista encerrados do
     * próximo ciclo" (fim do plantão + N min); `null` com o bloco desligado.
     */
    listedUntil?: string | null
    /** `'12:28'` — hora local da última carga. */
    lastLoadedLabel: string | null
    isRefreshing: boolean
    isExporting: boolean
    /**
     * Grupo operacional inferido pela página a partir da primeira resposta sem
     * filtro de grupo (Q23). `null` enquanto não há inferência segura.
     */
    inferredOperationalGroup?: string | null
    /** `facets.doctors` da resposta (ignora o próprio filtro de médico). */
    doctorOptions?: readonly PriorityDoctorFacetDto[]
    /** `counters.bySituation` (ignora o próprio filtro de situação). */
    situationCounts?: PrioritySituationCounts | null
    /** `antecipePaidUnconfirmed30d.count`; `null` enquanto não há resposta. */
    antecipePaidUnconfirmedCount?: number | null
    testId?: string
  }>(),
  {
    listedUntil: null,
    inferredOperationalGroup: null,
    doctorOptions: () => [],
    situationCounts: null,
    antecipePaidUnconfirmedCount: null,
    testId: 'priority-payments-filter-bar'
  }
)

const emit = defineEmits<{
  'update:filters': [patch: Partial<PriorityPaymentsFilters>]
  refresh: []
  export: []
  /** A2 (25/09): o chip "Antecipe pago sem horário" exporta a lista, não filtra. */
  'export-antecipe-paid-unconfirmed': []
}>()

const antecipePaidUnconfirmedLabel = computed(() =>
  props.antecipePaidUnconfirmedCount === null ? '—' : String(props.antecipePaidUnconfirmedCount)
)

/**
 * Sufixo do rótulo do "Ciclo atual" (B1, 25/09): a lista também traz os plantões
 * À vista do próximo ciclo já encerrados há N min, e o rótulo do ciclo sozinho
 * não explica essas linhas. Hora em Brasília, como o rótulo do período.
 */
const listedUntilText = computed(() => {
  if (props.filters.preset !== 'CURRENT' || !props.listedUntil) return null
  const hour = formatSlaHour(props.listedUntil)
  return hour === '—' ? null : `+ plantões À vista encerrados até ${hour}`
})

const updateFilters = (patch: Partial<PriorityPaymentsFilters>): void => {
  emit('update:filters', patch)
}

/**
 * O mockup tinha três opções; `PENDING` existe em PROD e o dono mandou
 * oferecê-lo — esconder um estado que chega nas linhas deixaria um recorte
 * inalcançável pelo filtro. Os rótulos são os mesmos de `PRIORITY_CHECK_LABELS`.
 */
const CHECK_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Qualquer' },
  { value: 'NONE', label: PRIORITY_CHECK_LABELS.NONE },
  { value: 'PARTIAL', label: PRIORITY_CHECK_LABELS.PARTIAL },
  { value: 'PENDING', label: PRIORITY_CHECK_LABELS.PENDING },
  { value: 'OK', label: PRIORITY_CHECK_LABELS.OK }
]

interface QuickFilter {
  key: string
  label: string
  isOn: (filters: PriorityPaymentsFilters) => boolean
  toggle: (filters: PriorityPaymentsFilters) => Partial<PriorityPaymentsFilters>
}

const QUICK_FILTERS: QuickFilter[] = [
  {
    key: 'rejections',
    label: '2+ rejeições',
    isOn: (f) => f.minRejections !== null,
    toggle: (f) => ({ minRejections: f.minRejections === null ? PRIORITY_MIN_REJECTIONS : null })
  },
  {
    key: 'nouser',
    label: 'Médico sem usuário',
    isOn: (f) => f.doctorWithoutUser,
    toggle: (f) => ({ doctorWithoutUser: !f.doctorWithoutUser })
  },
  {
    key: 'stalled',
    label: 'Parado há +24 h',
    isOn: (f) => f.stalledHours !== null,
    toggle: (f) => ({ stalledHours: f.stalledHours === null ? PRIORITY_STALLED_HOURS : null })
  }
]

/**
 * Legado: links antigos com `critical=true` ainda ligam `onlyCritical`. O chip
 * só aparece enquanto o filtro está ligado, para a pessoa ver e poder desligar.
 */
const LEGACY_CRITICAL_FILTER: QuickFilter = {
  key: 'critical',
  label: 'Só críticos',
  isOn: (f) => f.onlyCritical,
  toggle: (f) => ({ onlyCritical: !f.onlyCritical })
}

const visibleQuickFilters = computed<QuickFilter[]>(() =>
  props.filters.onlyCritical ? [...QUICK_FILTERS, LEGACY_CRITICAL_FILTER] : QUICK_FILTERS
)

/**
 * Combos de unidade e escala pelas rotas de filtro do PRÓPRIO ds-attendance
 * (`operations/filters/*`), as mesmas da fila de Registro de Plantão. O escopo
 * delas é `ds_attendance#attendance_emitting` via `@AccessibleResources`, o
 * MESMO da rota de pagamentos prioritários. `/healthUnits` e `/schedules`
 * exigem leitura de unidade/escala e davam 403 ao receptor puro.
 */
const dsAttendanceStore = useDsAttendanceStore()

const openFilter = ref<string | null>(null)
const groupOptions = ref<{ label: string; value: string }[]>([])

const toggleOpen = (key: string): void => {
  openFilter.value = openFilter.value === key ? null : key
}

const chipClass = (isOn: boolean): string =>
  isOn ? 'border-primary bg-primary text-white' : 'border-lightGray30 bg-white text-neutral50'

const unitOptions = computed(() =>
  dsAttendanceStore.filterHealthUnits.map((unit) => ({
    label: unit.title,
    value: String(unit.value)
  }))
)

const { notifyError, notifyErrorMessage } = useErrorToast()

/**
 * Frente G: opções de Setor e Posição pela rota do PRÓPRIO módulo
 * (`GET /priority-payments/filter-options`, escopo `attendance_emitting` = o
 * da listagem). Não recortadas por dado: a opção não some por não ter sido escolhida.
 */
const sectorOptions = ref<PrioritySectorOption[]>([])
const positionOptions = ref<PriorityPositionOption[]>([])

const sectorSelectOptions = computed(() =>
  buildSectorSelectOptions(sectorOptions.value, props.filters.healthUnits, props.filters.sectors)
)
const positionSelectOptions = computed(() =>
  buildPositionSelectOptions(positionOptions.value, props.filters.positions)
)
const selectedPositions = computed(() =>
  selectedPositionValues(positionOptions.value, props.filters.positions)
)

const scheduleOptions = computed(() =>
  dsAttendanceStore.filterSchedules.map((schedule) => ({
    label: schedule.title,
    value: String(schedule.value)
  }))
)

/** Reidrata o seletor com o intervalo vindo da URL (`preset=CUSTOM&startAt&endAt`). */
const customRange = computed(() =>
  props.filters.startAt && props.filters.endAt
    ? { start: new Date(props.filters.startAt), end: new Date(props.filters.endAt) }
    : null
)

function onPreset(preset: PriorityPeriodPreset): void {
  if (preset === 'LAST_7_DAYS') {
    updateFilters({ preset, ...lastSevenDaysRange(new Date()) })
    return
  }
  // `CURRENT`/`PREVIOUS` são resolvidos pelo servidor: mandar um intervalo velho
  // junto faria o backend ignorar o `cycle` e a tela mentir sobre o período.
  updateFilters({ preset, startAt: null, endAt: null })
}

function onCustomRange(range: { start: Date | null; end: Date | null }): void {
  if (!range.start || !range.end) return
  updateFilters({
    preset: 'CUSTOM',
    startAt: range.start.toISOString(),
    endAt: range.end.toISOString()
  })
}

const toggleInList = <T,>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value]

const onToggleGroup = (value: string): void => {
  hasTouchedGroupFilter = true
  updateFilters({
    operationalGroups: toggleInList(props.filters.operationalGroups, value)
  })
}

/** Q36: trocar a unidade poda, no MESMO patch, o setor que ficou sem unidade. */
const onToggleUnit = (value: string): void => {
  const healthUnits = toggleInList(props.filters.healthUnits, Number(value))
  const sectors = prunedSectorsForUnits(props.filters.sectors, healthUnits, sectorOptions.value)
  updateFilters(sectors === props.filters.sectors ? { healthUnits } : { healthUnits, sectors })
}

const onToggleSector = (value: string): void => {
  updateFilters({ sectors: toggleInList(props.filters.sectors, Number(value)) })
}

const onTogglePosition = (value: string): void => {
  updateFilters({
    positions: togglePositionOption(positionOptions.value, props.filters.positions, value)
  })
}

/**
 * Carga isolada: falha aqui (ou service ausente) não pode impedir unidade,
 * escala e grupo de preencherem. Depois da carga, um link com setor de outra
 * unidade é podado (Review Focus 1).
 */
const loadPlaceOptions = async (): Promise<void> => {
  try {
    const options = await Network.priorityPayments.filterOptions()
    sectorOptions.value = options.sectors
    positionOptions.value = options.positions
    const pruned = prunedSectorsForUnits(
      props.filters.sectors,
      props.filters.healthUnits,
      options.sectors
    )
    if (pruned !== props.filters.sectors) updateFilters({ sectors: pruned })
  } catch (error) {
    notifyError(error, 'Não foi possível carregar setores e posições')
  }
}

const onToggleSchedule = (value: string): void => {
  updateFilters({ schedules: toggleInList(props.filters.schedules, Number(value)) })
}

const onCheck = (value: string): void => {
  updateFilters({ checkStatus: (value || null) as PriorityCheckStatus | null })
}

/**
 * Médico (apontamento 2): opções de `facets.doctors`. Um médico selecionado que
 * não veio nas facetas (link antigo, teto de 500) continua na lista para poder
 * ser desmarcado.
 */
const doctorSelectOptions = computed(() => {
  const known = new Set(props.doctorOptions.map((doctor) => doctor.doctorId))
  return [
    ...props.doctorOptions.map((doctor) => ({
      label: `${doctor.doctorName} (${doctor.count})`,
      value: String(doctor.doctorId)
    })),
    ...props.filters.doctorIds
      .filter((id) => !known.has(id))
      .map((id) => ({ label: `Médico #${id}`, value: String(id) }))
  ]
})

const onToggleDoctor = (value: string): void => {
  updateFilters({ doctorIds: toggleInList(props.filters.doctorIds, Number(value)) })
}

/** Data do plantão (`s.date`): o picker devolve meia-noite LOCAL; `toDateOnly` não passa por UTC. */
const stationDateFilter = ref<{ reset?: () => void } | null>(null)

const stationDateRange = computed(() => {
  const start = parseLocalDate(props.filters.stationDateFrom)
  const end = parseLocalDate(props.filters.stationDateTo)
  return start && end ? { start, end } : null
})

const dayMonth = (isoDay: string): string => `${isoDay.slice(8, 10)}/${isoDay.slice(5, 7)}`

const stationDateTitle = computed(() => {
  const { stationDateFrom: from, stationDateTo: to } = props.filters
  if (!from || !to) return 'Data do plantão'
  return `Data do plantão: ${dayMonth(from)}–${dayMonth(to)}`
})

function onStationDate(range: { start: Date | null; end: Date | null }): void {
  if (!range.start || !range.end) return
  updateFilters({ stationDateFrom: toDateOnly(range.start), stationDateTo: toDateOnly(range.end) })
}

function onClearStationDate(): void {
  stationDateFilter.value?.reset?.()
  updateFilters({ stationDateFrom: null, stationDateTo: null })
}

function isSituationKnownByServer(situation: PrioritySituation): boolean {
  return !!props.situationCounts && situation in props.situationCounts
}

/** Situação (Q22, DO-18): o "filtro secundário por situação", com contagem. */
const situationOptions = computed(() =>
  PRIORITY_SITUATION_GROUPS.filter(
    // Q1 — o DTO do backend valida `situations` por enum: antes do backend conhecer
    // NOT_REQUIRED, oferecer o filtro daria 400. Só aparece quando os counters trazem a chave.
    (group) => group.key !== 'notRequired' || isSituationKnownByServer('NOT_REQUIRED')
  ).map((group) => {
    const counts = props.situationCounts
    const total = counts
      ? group.situations.reduce((sum, situation) => sum + (counts[situation] ?? 0), 0)
      : null
    return { label: total === null ? group.label : `${group.label} (${total})`, value: group.key }
  })
)

const selectedSituationKeys = computed(() =>
  PRIORITY_SITUATION_GROUPS.filter((group) =>
    group.situations.every((situation) => props.filters.situations.includes(situation))
  ).map((group) => group.key)
)

function onToggleSituationGroup(key: string): void {
  const group = PRIORITY_SITUATION_GROUPS.find((candidate) => candidate.key === key)
  if (!group) return
  updateFilters({ situations: toggleGroup(props.filters.situations, group.situations) })
}

/** Estado do SLA (Q22): multi, com "Não iniciado" e o atalho "Todos os pendentes". */
const PENDING_OPTION = 'PENDING'

const SLA_STATE_OPTIONS = [
  ...PRIORITY_SLA_STATES.map((state) => ({
    label: PRIORITY_SLA_STATE_LABELS[state],
    value: state
  })),
  { label: 'Todos os pendentes', value: PENDING_OPTION }
]

const hasAllPending = (states: PrioritySlaState[]): boolean =>
  PRIORITY_SLA_PENDING_STATES.every((state) => states.includes(state))

const selectedSlaValues = computed(() => [
  ...props.filters.slaStates,
  ...(hasAllPending(props.filters.slaStates) ? [PENDING_OPTION] : [])
])

function onToggleSlaState(value: string): void {
  const current = props.filters.slaStates
  updateFilters({
    slaStates:
      value === PENDING_OPTION
        ? toggleGroup(current, PRIORITY_SLA_PENDING_STATES)
        : toggleInList(current, value as PrioritySlaState)
  })
}

/**
 * Q23 — quando a pessoa só enxerga UM grupo operacional, a fila já abre
 * recortada nele. Quem infere o grupo é a página (a partir das linhas da
 * primeira resposta sem filtro de grupo): nem a listagem de escalas nem
 * `/field-options/operational-groups` (tabela global) sabem o grupo da pessoa.
 *
 * Pré-seleciona uma única vez por montagem, e só se a URL não trouxe grupo e a
 * pessoa ainda não mexeu no filtro: um link compartilhado ou uma escolha
 * explícita mandam mais do que a heurística.
 */
let hasPreselectedGroup = false
let hasTouchedGroupFilter = false

watch(
  () => props.inferredOperationalGroup,
  (group) => {
    if (!group || hasPreselectedGroup || hasTouchedGroupFilter) return
    if (props.filters.operationalGroups.length) return
    hasPreselectedGroup = true
    updateFilters({ operationalGroups: [group] })
  },
  { immediate: true }
)

onMounted(async () => {
  // Cargas independentes: uma falha não pode impedir a outra de preencher. A
  // store do ds-attendance não relança; o erro fica em `filterOptionsLoadError`.
  const [, groups] = await Promise.all([
    dsAttendanceStore.fetchFilterOptions().then(() => {
      if (dsAttendanceStore.filterOptionsLoadError)
        notifyErrorMessage(dsAttendanceStore.filterOptionsLoadError)
    }),
    new FieldOptionsService().getOperationalGroups().catch((error) => {
      notifyError(error, 'Não foi possível carregar os grupos operacionais')
      return []
    }),
    loadPlaceOptions()
  ])

  groupOptions.value = groups.map((group) => ({ label: group.name, value: group.name }))
})

/**
 * Busca com debounce: cada tecla dispararia uma listagem pesada no BE (e um
 * `router.replace`). O texto fica local enquanto a pessoa digita — o `:value`
 * preso a `filters.search` seria sobrescrito por qualquer re-render antes do
 * debounce — e só sobe depois de `SEARCH_DEBOUNCE_MS` sem digitar.
 */
const SEARCH_DEBOUNCE_MS = 300
const searchText = ref(props.filters.search)
let searchDebounce: ReturnType<typeof setTimeout> | null = null

const cancelSearchDebounce = (): void => {
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = null
}

function onSearchInput(value: string): void {
  searchText.value = value
  cancelSearchDebounce()
  searchDebounce = setTimeout(() => {
    searchDebounce = null
    updateFilters({ search: value })
  }, SEARCH_DEBOUNCE_MS)
}

// Mudança de fora (limpar filtros, URL): o campo acompanha e a busca pendente cai.
watch(
  () => props.filters.search,
  (search) => {
    if (search === searchText.value) return
    cancelSearchDebounce()
    searchText.value = search
  }
)

onBeforeUnmount(cancelSearchDebounce)
</script>
